import { create } from 'zustand';
import { AuthState, LoginCredentials } from '@/types';
import api from '@/services/api';

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (credentials: LoginCredentials) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post('/auth/login', credentials);

      if (response.data.success && response.data.token) {
        // Save token to localStorage
        localStorage.setItem('auth_token', response.data.token);

        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        set({
          error: response.data.error || 'Login failed',
          isLoading: false,
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Login failed. Please check your credentials.';
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  loginWithGoogle: async (credential: string) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post('/auth/google', { credential });

      if (response.data.success && response.data.token) {
        // Save token to localStorage
        localStorage.setItem('auth_token', response.data.token);

        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        set({
          error: response.data.error || 'Google login failed',
          isLoading: false,
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Google login failed. Please try again.';
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      // Remove token from localStorage
      localStorage.removeItem('auth_token');

      await api.post('/auth/logout');
      set({
        user: null,
        isAuthenticated: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      // Still clear local state and token even if API call fails
      localStorage.removeItem('auth_token');
      set({
        user: null,
        isAuthenticated: false,
        error: null,
      });
    }
  },

  checkAuth: async () => {
    try {
      set({ isLoading: true });

      // Check if token exists in localStorage
      const token = localStorage.getItem('auth_token');

      if (!token) {
        // No token, user is not authenticated
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
        return;
      }

      // Token exists, verify with backend
      const response = await api.get('/auth/check');

      if (response.data.authenticated && response.data.user) {
        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        // Token invalid, clear it
        localStorage.removeItem('auth_token');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    } catch (error: any) {
      // Token validation failed, clear it
      localStorage.removeItem('auth_token');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
